<template>
  <div class="row">
    <div class="col-12">
      <form-separator
        v-if="!showPane && showFormSeparator"
        margin-size="sm">
        <template #icon>
          <IconColoredAudienceLogic />
        </template>
        <span>{{ capitalize(i18n.t('names.summary', 1)) }}</span>
      </form-separator>
      <div
        v-else-if="showFormSeparator"
        class="pane-header">
        <span class="icon"><IconColoredAudienceLogic /></span>
        <span>{{ capitalize(i18n.t('names.summary', 1)) }}</span>
      </div>
    </div>
    <div class="col-12">
      <div :class="{ 'pad-top': true, 'pane-content': showPane }">
        <div
          class="row"
          :class="{ 'mt-3': showPane }">
          <div class="col-12">
            <div
              v-if="isEpsilonRuleAdded"
              class="custom-audience-cpm">
              <icon-colored-c-p-m class="max-cpm-icon" />
              <span
                v-tooltip.bottom="capitalize($t('messages.highestCPMOutOfAllEpsilonSegments'))"
                class="max-cpm-text">
                {{ capitalize($t('messages.highestEpsilonCPM')) }}:
                <strong>{{ maxCPMFromSelectedEpsilonSegments }}</strong>
              </span>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-12">
            <div
              v-sanitize-html="audienceLogic.userFriendlyLogicalExpressionHtml"
              class="well formula"
              :class="{
                dark: dark,
                light: !dark,
                center: center,
                'no-border': !showBorder,
              }"
              :data-test-content="audienceLogic.userFriendlyLogicalExpression"
              :data-test-content-alt="audienceLogic.userFriendlyBackendLogicalExpression" />
            <div
              v-if="debugMode"
              class="debug-backend">
              <div class="alert alert-info flex-h">
                <icon-info />
                <div>{{ capitalize($t('messages.ruleTheWayToTheBackend')) }}</div>
              </div>
              <div
                v-sanitize-html="audienceLogic.userFriendlyBackendLogicalExpressionHtml"
                class="well formula"
                :class="{
                  dark: dark,
                  light: !dark,
                }"
                :data-test-content="audienceLogic.userFriendlyBackendLogicalExpression" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { capitalize, computed } from 'vue';
  import { useI18n } from 'vue-i18n';

  import FormSeparator from '@/common/components/controls/FormSeparator.vue';
  import { AudienceRuleModes } from '@/common/enums/audience-rule-modes';
  import { IconColoredAudienceLogic, IconColoredCPM } from '@/common/icons/colored';
  import { IconInfo } from '@/common/icons/monochrome';
  import { AudienceLogic } from '@/common/models';
  import { mapState } from '@/utils/StateMapping';

  interface Props {
    showPane?: boolean;
    showBorder?: boolean;
    audienceLogic: AudienceLogic;
    showFormSeparator?: boolean;
    dark?: boolean;
    center?: boolean;
  }

  //
  // Setup
  //
  const i18n = useI18n();
  const props = withDefaults(defineProps<Props>(), {
    showPane: true,
    showBorder: true,
    showFormSeparator: true,
    dark: true,
    center: false,
  });

  //
  // Computed
  //
  const isEpsilonRuleAdded = computed(() =>
    props.audienceLogic?.audienceRules?.some((rule) => rule.mode === AudienceRuleModes.Epsilon && rule.segments),
  );
  const maxCPMFromSelectedEpsilonSegments = computed(() => {
    if (isEpsilonRuleAdded.value) {
      const epsilonSegments = props.audienceLogic.audienceRules
        .filter((a) => a.mode === AudienceRuleModes.Epsilon && a.segment)
        .map((a) => a.segment?.cpm);
      return epsilonSegments.length ? i18n.n(Math.max(...epsilonSegments), 'currency') : null;
    }
    return null;
  });

  //
  // Methods
  //
  const { debugMode } = mapState();
</script>

<style lang="scss">
  @use '@/styles/variables' as *;

  .well.formula {
    &.no-border {
      border: none !important;
      box-shadow: none !important;
    }

    &.center {
      width: fit-content;
      margin: 0 auto;
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      align-items: center;
      justify-content: center;
      column-gap: 0.6rem;
    }

    &.light {
      color: var(--infoLight) !important;
      background: unset !important;

      .rule {
        border: 1px solid var(--infoLight);
      }
    }
  }

  .custom-audience-cpm {
    display: flex;
    flex-direction: row;
    align-items: center;
    margin-bottom: 2rem;

    .max-cpm-icon {
      height: 2rem;
    }

    .max-cpm-text {
      margin: 0 0 0 1rem;
      font-size: 1.4rem;
    }
  }

  .debug-backend {
    svg {
      height: 2rem;
      margin-right: 1rem;
    }
  }
</style>
